import React, { cloneElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// One entry point for mouse, keyboard and touch; no persistent row action icons.
// An optional `button` renders one discoverable trigger beside the row (hover, focus
// or a coarse pointer), opening the same menu as right-click, long-press and Shift+F10.
export default function HistoryMenu({ children, label, items, button = null }) {
  const [position, setPosition] = useState(null);
  const menu = useRef(null), trigger = useRef(null), toggle = useRef(null), press = useRef(null), suppress = useRef(false);
  const close = () => { setPosition(null); trigger.current?.focus(); };
  const cancelPress = () => { clearTimeout(press.current?.timer); press.current = null; };
  useEffect(() => cancelPress, []);
  useLayoutEffect(() => {
    if (!position || !menu.current) return;
    const node = menu.current, rect = node.getBoundingClientRect();
    node.style.left = `${Math.max(8, Math.min(position.x, window.innerWidth - rect.width - 8))}px`;
    node.style.top = `${Math.max(8, Math.min(position.y, window.innerHeight - rect.height - 8))}px`;
    (node.querySelector('button:not(:disabled)') || node).focus();
  }, [position]);
  useEffect(() => {
    if (!position) return;
    const outside = event => {
      // The visible trigger toggles its own menu; treat it as inside.
      if (menu.current?.contains(event.target) || toggle.current?.contains(event.target)) return;
      close();
    };
    const dismiss = () => setPosition(null);
    document.addEventListener('pointerdown', outside);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => { document.removeEventListener('pointerdown', outside); window.removeEventListener('resize', dismiss); window.removeEventListener('scroll', dismiss, true); };
  }, [position]);
  const open = (node, x, y) => { trigger.current = node; setPosition({ x, y }); };
  return <>{cloneElement(children, {
    'aria-haspopup': 'menu',
    onContextMenu: event => { event.preventDefault(); cancelPress(); open(event.currentTarget, event.clientX, event.clientY); },
    onKeyDown: event => {
      if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) {
        event.preventDefault(); const rect = event.currentTarget.getBoundingClientRect(); open(event.currentTarget, rect.x, rect.bottom);
      }
    },
    onPointerDown: event => {
      suppress.current = false;
      if (event.pointerType !== 'touch') return;
      const node = event.currentTarget, {clientX: x, clientY: y} = event;
      cancelPress(); press.current = { x, y, timer: setTimeout(() => { suppress.current = true; open(node, x, y); }, 500) };
    },
    onPointerMove: event => { if (press.current && Math.hypot(event.clientX - press.current.x, event.clientY - press.current.y) > 8) cancelPress(); },
    onPointerUp: cancelPress, onPointerCancel: cancelPress,
    onClick: event => { if (suppress.current) { event.preventDefault(); suppress.current = false; } else children.props.onClick?.(event); },
  })}{button && items.length > 0 && <button ref={toggle} type="button" className={button.className}
    aria-label={button.label} aria-haspopup="menu" aria-expanded={!!position} disabled={button.disabled}
    onClick={event => {
      if (position) return setPosition(null);
      const rect = event.currentTarget.getBoundingClientRect();
      open(event.currentTarget, rect.right, rect.bottom + 4);
    }}>{button.icon}</button>}{position && createPortal(<div ref={menu} className="history-context-menu" role="menu" tabIndex={-1} aria-label={label}
    style={{ left: position.x, top: position.y }} onContextMenu={event => event.preventDefault()} onKeyDown={event => {
      if (event.key === 'Escape' || event.key === 'Tab') { event.preventDefault(); event.stopPropagation(); close(); }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault(); const nodes = [...menu.current.querySelectorAll('button:not(:disabled)')];
        const index = nodes.indexOf(document.activeElement), step = event.key === 'ArrowUp' ? -1 : 1;
        nodes[event.key === 'Home' ? 0 : event.key === 'End' ? nodes.length - 1 : (index + step + nodes.length) % nodes.length]?.focus();
      }
    }}>{items.map(item => <button key={item.label} role="menuitem" disabled={item.disabled} className={item.danger ? 'danger' : ''}
      onClick={() => { close(); item.action(); }}>{item.label}</button>)}</div>, document.querySelector('.original-demo') || document.body)}</>;
}
